import React from "react";
import { X, TrendingUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";

/**
 * Historique des prix d'achat d'un ingrédient (Étape 5).
 * Lecture seule — les lignes sont créées automatiquement côté backend à
 * chaque changement de purchasePriceHt/Ttc (jamais de saisie manuelle ici).
 */
const PurchasePriceHistoryModal = ({ productId, productName, onClose }) => {
    const { data, isLoading, error } = useQuery({
        queryKey: ['purchase-price-history', productId],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get(`/products/${productId}/purchase-price-history`, {
                headers: { 'X-account-id': accountId.id }
            });
            return response.data;
        },
        enabled: !!productId
    });

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" />
                        Historique des prix d'achat — {productName}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto">
                    {isLoading && <p className="text-sm text-gray-400">Chargement...</p>}
                    {error && <p className="text-sm text-red-500">Erreur lors du chargement de l'historique</p>}

                    {data && (
                        <>
                            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Prix d'achat actuel</div>
                                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                    {data.current_purchase_price_ht != null ? `${Number(data.current_purchase_price_ht).toFixed(2)} DH HT` : '—'}
                                    {data.current_purchase_price_ttc != null && (
                                        <span className="text-gray-400"> / {Number(data.current_purchase_price_ttc).toFixed(2)} DH TTC</span>
                                    )}
                                </div>
                            </div>

                            {data.data.length === 0 ? (
                                <p className="text-sm text-gray-400">Aucun changement de prix enregistré pour l'instant.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                            <th className="py-2">Date</th>
                                            <th className="py-2 text-right">Prix HT</th>
                                            <th className="py-2 text-right">Prix TTC</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                        {data.data.map(entry => (
                                            <tr key={entry.id}>
                                                <td className="py-2 text-gray-700 dark:text-gray-300">
                                                    {new Date(entry.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                </td>
                                                <td className="py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                                                    {entry.purchase_price_ht != null ? `${Number(entry.purchase_price_ht).toFixed(2)} DH` : '—'}
                                                </td>
                                                <td className="py-2 text-right text-gray-500 dark:text-gray-400">
                                                    {entry.purchase_price_ttc != null ? `${Number(entry.purchase_price_ttc).toFixed(2)} DH` : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PurchasePriceHistoryModal;
